import type { ReactNode } from 'react';
import type { ShippingCountdown } from './types';

/**
 * "Time left to confirm shipping" caption + two tinted `h / m` chips —
 * Figma 703:28734. Optional header of `ShippingModal`; rendered only
 * when the caller passes a `countdown` (the My Packs ship flow omits it).
 *
 * Every surface is themed under the single `--co-ShippingModal-*`
 * namespace so the whole modal collapses to ONE Design Constructor entry.
 * The chips use `tabular-nums` so the column doesn't shimmer as the
 * minute rolls over. Colour fallbacks go through `--ship-default-*`, which
 * only sleek's dark sheet sets; elsewhere they resolve to the light values.
 */
export function ShippingCountdownHeader({ countdown }: { countdown: ShippingCountdown }) {
  const { hours, minutes, expired } = countdown;

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center">
      <span
        style={{
          color: 'var(--co-ShippingModal-countdownLabelColor, var(--ship-default-countdownLabelColor, rgba(0,0,0,0.85)))',
          fontFamily:
            'var(--co-ShippingModal-countdownLabelFont, "Inter"), Inter, sans-serif',
          fontSize: 'var(--co-ShippingModal-countdownLabelSize, 15px)',
          // Unitless so line-height tracks the size knob instead of
          // clipping the moment a partner bumps the font past 20 px.
          lineHeight: 1.35,
        }}
      >
        Time left to confirm shipping
      </span>
      <span className="inline-flex items-center gap-2">
        <Chip>{expired ? '0h' : `${hours}h`}</Chip>
        <Chip>{expired ? '0m' : `${minutes}m`}</Chip>
      </span>
    </div>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span
      className="inline-flex items-center rounded-[8px] px-2.5 py-1 font-medium tabular-nums"
      style={{
        backgroundColor: 'var(--co-ShippingModal-chipFill, var(--ship-default-chipFill, rgba(0,0,0,0.07)))',
        color: 'var(--co-ShippingModal-chipText, var(--ship-default-chipText, rgba(0,0,0,0.85)))',
        fontFamily: 'var(--co-ShippingModal-chipFont, "Inter"), Inter, sans-serif',
        fontSize: 'var(--co-ShippingModal-chipSize, 15px)',
        lineHeight: 1.35,
      }}
    >
      {children}
    </span>
  );
}
